import { useEffect, useState } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import { get, post } from "../../utils/apiManager";
import moment from "moment";

const pill = (label, active) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '3px 10px', borderRadius: 100, fontSize: 12, fontWeight: 700,
    background: active ? '#dcfce7' : '#fee2e2',
    color: active ? '#15803d' : '#b91c1c',
  }}>
    <span style={{ fontSize: 7 }}>●</span>{active ? 'Active' : 'Inactive'}
  </span>
);

const InfoRow = ({ label, value }) => value ? (
  <div style={{ display: 'flex', padding: '10px 0', borderBottom: '1px solid #f0f2f8' }}>
    <div style={{ width: 160, flexShrink: 0, fontSize: 13, fontWeight: 600, color: '#9aa5c0' }}>{label}</div>
    <div style={{ fontSize: 14, color: '#0a1628' }}>{value}</div>
  </div>
) : null;

const Card = ({ title, children, onBack }) => (
  <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #edf0f7', boxShadow: '0 1px 4px rgba(26,39,68,.05)', overflow: 'hidden', marginBottom: 20 }}>
    <div style={{ padding: '16px 24px', borderBottom: '1px solid #edf0f7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0a1628', margin: 0 }}>{title}</h3>
      {onBack && (
        <button onClick={onBack} style={{ background: '#f0f2f8', color: '#5a6a8a', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
          ← Back to Companies
        </button>
      )}
    </div>
    <div style={{ padding: '4px 24px 16px' }}>{children}</div>
  </div>
);

const ViewOne = () => {
  const location = useLocation();
  const Navigate = useNavigate();
  const [company, setCompany] = useState({});
  const [togglingWhiteLabel, setTogglingWhiteLabel] = useState(false);
  const companyId = location?.search?.split("?")[1];

  useEffect(() => {
    if (companyId) getOneCompanies(companyId);
  }, [companyId]);

  const getOneCompanies = (id) => {
    get(`/company/${id}`)
      .then((res) => setCompany(res?.datas || {}))
      .catch((err) => console.log(err));
  };

  const handleWhiteLabelToggle = async () => {
    if (!company?._id) return;
    setTogglingWhiteLabel(true);
    const next = !company.whiteLabelEnabled;
    const res = await post('/company/white-label-toggle', { id: company._id, whiteLabelEnabled: next });
    setTogglingWhiteLabel(false);
    if (res?.status) {
      setCompany((prev) => ({ ...prev, whiteLabelEnabled: next }));
    } else {
      alert(res?.message || 'Failed to update White Label setting');
    }
  };

  return (
    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}>

      {/* Company Details */}
      <Card title="Company Details" onBack={() => Navigate('/admin/user')}>
        <InfoRow label="Name" value={company?.firstName ? `${company.firstName} ${company.lastName}` : null} />
        <InfoRow label="Company Name" value={company?.companyName} />
        <InfoRow label="Company Type" value={company?.companyType} />
        <InfoRow label="Email" value={company?.email} />
        <InfoRow label="Phone" value={company?.phone} />
        <InfoRow label="Cell Phone" value={company?.cellPhone} />
        <InfoRow label="Address" value={company?.address} />
        <InfoRow label="Apt Suite" value={company?.addressAptSuite} />
        <InfoRow label="City" value={company?.city} />
        <InfoRow label="State" value={company?.state} />
        <InfoRow label="Zip Code" value={company?.zipCode} />
        <InfoRow label="Website" value={company?.website} />
        <InfoRow label="Bid Slug" value={company?.bidSlug} />
        <div style={{ display: 'flex', padding: '10px 0', borderBottom: '1px solid #f0f2f8', alignItems: 'center' }}>
          <div style={{ width: 160, flexShrink: 0, fontSize: 13, fontWeight: 600, color: '#9aa5c0' }}>White Label</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {pill(company?.whiteLabelEnabled ? 'Enabled' : 'Disabled', company?.whiteLabelEnabled)}
            <button
              type="button"
              onClick={handleWhiteLabelToggle}
              disabled={togglingWhiteLabel}
              style={{
                background: company?.whiteLabelEnabled ? '#fee2e2' : '#dcfce7',
                color: company?.whiteLabelEnabled ? '#b91c1c' : '#15803d',
                border: 'none', borderRadius: 8, padding: '7px 14px',
                fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              {togglingWhiteLabel ? '…' : company?.whiteLabelEnabled ? 'Disable' : 'Enable'}
            </button>
          </div>
        </div>
        {company?.hasOwnProperty('isActive') && (
          <div style={{ display: 'flex', padding: '10px 0' }}>
            <div style={{ width: 160, flexShrink: 0, fontSize: 13, fontWeight: 600, color: '#9aa5c0' }}>Status</div>
            {pill('Status', company?.isActive)}
          </div>
        )}
      </Card>

      {/* Staff Details */}
      {company?.staffDetails && (
        <Card title="Staff Details">
          <InfoRow label="Name" value={company?.staffDetails?.name} />
          <InfoRow label="Email" value={company?.staffDetails?.email} />
          <InfoRow label="Phone" value={company?.staffDetails?.phone} />
          <InfoRow label="Birth Date" value={company?.staffDetails?.birthDate ? moment(company.staffDetails.birthDate).format("DD MMMM YYYY") : null} />
          <InfoRow label="Position" value={company?.staffDetails?.position} />
          <InfoRow label="Address" value={company?.staffDetails?.address} />
          <InfoRow label="City" value={company?.staffDetails?.city} />
          <InfoRow label="State" value={company?.staffDetails?.state} />
          <InfoRow label="Zip Code" value={company?.staffDetails?.zipCode} />
          <InfoRow label="Notes" value={company?.staffDetails?.notes} />
          <InfoRow label="Others" value={company?.staffDetails?.others} />
          {company?.staffDetails?.hasOwnProperty('isActive') && (
            <div style={{ display: 'flex', padding: '10px 0' }}>
              <div style={{ width: 160, flexShrink: 0, fontSize: 13, fontWeight: 600, color: '#9aa5c0' }}>Status</div>
              {pill('Status', company?.staffDetails?.isActive)}
            </div>
          )}
        </Card>
      )}

      {/* Consignee Details */}
      <Card title="Consignee Details">
        <InfoRow label="Name" value={company?.firstName ? `${company.firstName} ${company.lastName}` : null} />
        <InfoRow label="Company Name" value={company?.companyName} />
        <InfoRow label="Company Type" value={company?.companyType} />
        <InfoRow label="Email" value={company?.email} />
        <InfoRow label="Phone" value={company?.phone} />
        <InfoRow label="Cell Phone" value={company?.cellPhone} />
        <InfoRow label="Address" value={company?.address} />
        <InfoRow label="Apt Suite" value={company?.addressAptSuite} />
        <InfoRow label="City" value={company?.city} />
        <InfoRow label="State" value={company?.state} />
        <InfoRow label="Zip Code" value={company?.zipCode} />
        <InfoRow label="Website" value={company?.website} />
        {company?.hasOwnProperty('isActive') && (
          <div style={{ display: 'flex', padding: '10px 0' }}>
            <div style={{ width: 160, flexShrink: 0, fontSize: 13, fontWeight: 600, color: '#9aa5c0' }}>Status</div>
            {pill('Status', company?.isActive)}
          </div>
        )}
      </Card>
    </div>
  );
};

export default ViewOne;
